import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Check, X, AlertTriangle, Send, CheckCircle2, Share2, Loader2, Sparkles } from 'lucide-react';
import apiClient from '../../api/client';
import { streamChatReply } from '../../api/testing';
import { useToast } from '../../components/ui/toastContext';
import DistillSuggestionsPanel from '../../components/DistillSuggestionsPanel';

interface Message {
  id: string;
  role: 'user' | 'agent';
  content: string;
  feedback?: { rating: string };
  created_at: string;
}

interface AgentInfo {
  llm_config_id?: string | null;
}

export default function DialogueTeaching() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [distillOpen, setDistillOpen] = useState(false);
  const [agentConfig, setAgentConfig] = useState<AgentInfo | null>(null);
  const chatEnd = useRef<HTMLDivElement>(null);

  const loadAgentConfig = useCallback(async () => {
    try {
      const res = await apiClient.get(`/agents/${agentId}`);
      setAgentConfig(res.data);
    } catch (e) {
      console.error('Failed to load agent:', e);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadAgentConfig();
    };
    tick();
  }, [loadAgentConfig]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startSession = async () => {
    try {
      const res = await apiClient.post(`/projects/${agentId}/sessions`, {
        project_id: agentId,
        session_type: 'dialogue'
      });
      setSessionId(res.data.id);
    } catch {
      // Fallback: create session via agents endpoint
      const res = await apiClient.post(`/agents/${agentId}/sessions`, {
        session_type: 'dialogue'
      });
      setSessionId(res.data.id);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    try {
      // Save user message
      const res = await apiClient.post(`/sessions/${sessionId}/messages`, {
        role: 'user',
        content: userMessage
      });

      const newMessages = [...messages, {
        id: res.data.id,
        role: 'user' as const,
        content: userMessage,
        created_at: new Date().toISOString()
      }];
      setMessages(newMessages);

      // P3-④：优先走 SSE 流式回复（逐 token 渲染）；失败回退 REST /chat 保持可用性
      const streamId = `stream-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: streamId,
        role: 'agent' as const,
        content: '',
        created_at: new Date().toISOString()
      }]);

      let streamError: string | null = null;
      try {
        await streamChatReply(sessionId, { message: userMessage, agent_id: agentId }, {
          // TOKEN 帧逐字追加到当前气泡
          onToken: (chunk) => {
            setMessages(prev => prev.map(m => (m.id === streamId ? { ...m, content: m.content + chunk } : m)));
          },
          // DONE 帧收尾：落库消息 id 回填（反馈评价按钮依赖真实 id）
          onDone: (done) => {
            if (done.message_id) {
              setMessages(prev => prev.map(m => (m.id === streamId ? { ...m, id: done.message_id as string } : m)));
            }
          },
          onError: (content) => { streamError = content; },
        });
      } catch (streamExc) {
        console.error('SSE chat stream failed, fallback to REST /chat:', streamExc);
        streamError = streamExc instanceof Error ? streamExc.message : String(streamExc);
      }

      if (streamError !== null) {
        // 流式失败（连接失败 / 中道中断）：回退 REST /chat；
        // 先清空可能已渲染的半截内容，避免与 REST 全量回复重复
        try {
          const chatRes = await apiClient.post(`/sessions/${sessionId}/chat`, {
            message: userMessage,
            agent_id: agentId
          });
          setMessages(prev => prev.map(m => (m.id === streamId ? {
            ...m,
            id: chatRes.data.id,
            content: chatRes.data.content,
          } : m)));
        } catch (llmError) {
          // If no LLM configured, show fallback
          console.error('LLM call failed:', llmError);
          setMessages(prev => prev.map(m => (m.id === streamId ? {
            ...m,
            content: '请先为该 Agent 配置 LLM，然后在对话示教页面进行测试。',
          } : m)));
        }
      }
    } catch (e) {
      console.error('Failed to send message:', e);
      toast.error('发送消息失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const addFeedback = async (msgId: string, rating: string) => {
    if (!sessionId) return;
    try {
      await apiClient.post(`/sessions/${sessionId}/messages/${msgId}/feedback`, {
        rating,
        confidence: 1.0
      });
      setMessages(messages.map(m =>
        m.id === msgId ? { ...m, feedback: { rating } } : m
      ));
    } catch (e) {
      console.error('Failed to add feedback:', e);
      toast.error('评价失败，请稍后重试');
    }
  };

  const shareSession = async () => {
    if (!sessionId || sharing) return;
    setSharing(true);
    try {
      const res = await apiClient.post('/shares', {
        resource_type: 'session',
        resource_id: sessionId,
      });
      const url = `${window.location.origin}/share/${res.data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('会话分享链接已复制到剪贴板（24 小时内有效）');
      } catch {
        // 剪贴板不可用（非安全上下文等）时直接展示链接
        toast.info(`分享链接：${url}`);
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('创建分享失败' + (typeof detail === 'string' ? `: ${detail}` : ''));
    } finally {
      setSharing(false);
    }
  };

  const completeSession = async () => {
    if (!sessionId) return;
    try {
      await apiClient.post(`/sessions/${sessionId}/complete`);
    } catch (e) {
      console.error('Failed to complete session:', e);
      toast.error('完成会话失败，请稍后重试');
    }
    navigate(`/agents/${agentId}`);
  };

  if (!sessionId) {
    return (
      <div className="container">
        <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
          <ArrowLeft size={16} /> 返回概览
        </button>

        <div className="card text-center" style={{ maxWidth: 500, margin: '60px auto' }}>
          <h2><MessageSquare size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />对话示教</h2>
          <p>通过与 Agent 模拟对话来教会它回答问题的方式。</p>

          {!agentConfig?.llm_config_id && (
            <div className="badge warning mb-4" style={{ display: 'block', padding: '12px' }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 尚未配置 LLM，请先在设置中配置
            </div>
          )}

          <button
            className="primary"
            onClick={startSession}
            style={{ padding: '16px 32px', fontSize: '16px' }}
          >
            开始示教
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <div className="flex items-center justify-between mb-4">
        <button className="secondary" onClick={() => navigate(`/agents/${agentId}`)}>
          <ArrowLeft size={16} /> 返回概览
        </button>
        <h2><MessageSquare size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />对话示教</h2>
        <div className="flex gap-2">
          <button className="secondary" onClick={() => setDistillOpen(true)}>
            <Sparkles size={16} /> 蒸馏建议
          </button>
          <button className="secondary" onClick={shareSession} disabled={sharing}>
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} 分享会话
          </button>
          <button className="secondary" onClick={() => { completeSession(); setSessionId(null); }}>
            完成示教
          </button>
        </div>
      </div>

      <DistillSuggestionsPanel
        sessionId={sessionId}
        agentId={agentId ?? ''}
        open={distillOpen}
        onClose={() => setDistillOpen(false)}
      />

      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="chat-messages" style={{ flex: 1, overflow: 'auto' }}>
          {messages.length === 0 && (
            <div className="empty" style={{ padding: '40px' }}>
              <p>开始对话，教会 Agent 正确的回答方式</p>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={`message ${msg.role}`}>
              <div>{msg.content}</div>
              {msg.role === 'agent' && !msg.feedback && (
                <div className="flex gap-2 mt-2" style={{ opacity: 0.8 }}>
                  <button
                    onClick={() => addFeedback(msg.id, 'correct')}
                    style={{ padding: '4px 8px', fontSize: '12px', background: 'rgba(126,231,135,0.2)', color: 'var(--color-success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Check size={12} /> 正确
                  </button>
                  <button
                    onClick={() => addFeedback(msg.id, 'incorrect')}
                    style={{ padding: '4px 8px', fontSize: '12px', background: 'rgba(248,81,73,0.2)', color: 'var(--color-error)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <X size={12} /> 错误
                  </button>
                  <button
                    onClick={() => addFeedback(msg.id, 'partial')}
                    style={{ padding: '4px 8px', fontSize: '12px', background: 'rgba(240,136,62,0.2)', color: 'var(--color-warning)' }}
                  >
                    <AlertTriangle size={12} /> 部分
                  </button>
                </div>
              )}
              {msg.feedback && (
                <small style={{ opacity: 0.6, display: 'block', marginTop: '4px' }}>
                  已评价: {msg.feedback.rating === 'correct' ? <><CheckCircle2 size={12} /> 正确</> : msg.feedback.rating === 'incorrect' ? <><X size={12} /> 错误</> : <><AlertTriangle size={12} /> 部分正确</>}
                </small>
              )}
            </div>
          ))}
          {loading && (
            <div className="message agent">
              <div className="flex items-center gap-2">
                <div className="spinner" style={{ width: 16, height: 16 }}></div>
                <span>正在思考...</span>
              </div>
            </div>
          )}
          <div ref={chatEnd} />
        </div>

        <div className="chat-input">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="输入你的问题或指导..."
            onKeyDown={e => e.key === 'Enter' && !loading && sendMessage()}
            disabled={loading}
          />
          <button className="primary" onClick={sendMessage} disabled={loading || !input.trim()}>
            <Send size={14} /> 发送
          </button>
        </div>
      </div>
    </div>
  );
}